"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { getPublicImageUrl } from "@/utils/imageUtils";
import { galleryCustomizeHref } from "@/utils/galleryNavigation";

interface MiniGalleryDress {
  id: string;
  prompt: string | null;
  image_url: string | null;
  original_image_path?: string | null;
  back_image_url?: string | null;
  back_image_path?: string | null;
  color?: string | null;
  back_prompt?: string | null;
  preset_type?: string | null;
  dress_length?: string | null;
  sleeve_style?: string | null;
  neckline_style?: string | null;
  waistline_style?: string | null;
  silhouette_fit?: string | null;
  fabric_texture?: string | null;
}

interface MiniGalleryStripProps {
  title?: string;
  browseAllText?: string;
  browseAllHref?: string;
  className?: string;
  initialData?: MiniGalleryDress[];
}

export default function MiniGalleryStrip({
  title = "See Examples",
  browseAllText = "Browse all",
  browseAllHref = "/gallery",
  className = "",
  initialData = [],
}: MiniGalleryStripProps) {
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
  const router = useRouter();
  const [hasEnteredViewport, setHasEnteredViewport] = useState<boolean>(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [galleryDresses, setGalleryDresses] =
    useState<MiniGalleryDress[]>(initialData);
  const [loadingGallery, setLoadingGallery] = useState<boolean>(
    initialData.length === 0
  );

  // Defer work until the section is visible (10% in view)
  useEffect(() => {
    if (hasEnteredViewport) return;
    const target = sectionRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasEnteredViewport]);

  // Fetch a small set of recent gallery dresses for the homepage strip
  // Skip if we already have initial data from server-side rendering
  useEffect(() => {
    // If we have initial data, don't fetch again
    if (initialData.length > 0) {
      return;
    }
    if (!hasEnteredViewport) {
      return;
    }

    let isMounted = true;
    const fetchMiniGallery = async () => {
      try {
        setLoadingGallery(true);
        // Fetch gallery data with standard client-side fetch
        const res = await fetch(`/api/gallery?page=1&limit=6`);
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted) {
          setGalleryDresses(
            (data?.dresses || []).map((d: any) => ({
              id: d.id,
              prompt: d.prompt ?? null,
              image_url: d.image_url ?? null,
              original_image_path: d.original_image_path ?? null,
              back_image_url: d.back_image_url ?? null,
              back_image_path: d.back_image_path ?? null,
              color: d.color ?? null,
              back_prompt: d.back_prompt ?? null,
              preset_type: d.preset_type ?? null,
              dress_length: d.dress_length ?? null,
              sleeve_style: d.sleeve_style ?? null,
              neckline_style: d.neckline_style ?? null,
              waistline_style: d.waistline_style ?? null,
              silhouette_fit: d.silhouette_fit ?? null,
              fabric_texture: d.fabric_texture ?? null,
            }))
          );
        }
      } catch {
        // silent fail for homepage strip
      } finally {
        if (isMounted) setLoadingGallery(false);
      }
    };
    fetchMiniGallery();

    return () => {
      isMounted = false;
    };
  }, [hasEnteredViewport, initialData.length]);

  const handleCustomize = useCallback(
    (dress: MiniGalleryDress) => {
      router.push(galleryCustomizeHref(dress, "mini_gallery"));
    },
    [router]
  );

  return (
    <section
      className={`bg-[var(--background)] py-16 sm:py-24 no-shift-mini-gallery ${className}`}
      ref={sectionRef}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
            {title}
          </h3>
          <Link
            href={browseAllHref}
            className="text-sm font-medium text-primary hover:text-primary hover:underline"
          >
            {browseAllText}
          </Link>
        </div>

        <div className="relative">
          <div className="flex gap-4 overflow-x-auto pb-4 pt-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
            {(!hasEnteredViewport
              ? Array.from<undefined>({ length: 6 })
              : loadingGallery
                ? Array.from<undefined>({ length: 6 })
                : galleryDresses
            ).map((dress: MiniGalleryDress | undefined, idx) => {
              const isHovered = hoveredCardIndex === idx;
              return (
                <div
                  key={
                    !hasEnteredViewport || loadingGallery
                      ? `skeleton-${idx}`
                      : dress?.id
                  }
                  className="shrink-0 w-56 sm:w-52 md:w-56 p-2"
                  style={{
                    transform: isHovered ? "scale(1.05)" : "scale(1)",
                    transition: "transform 300ms ease-out",
                    zIndex: isHovered ? 10 : 1,
                  }}
                >
                  <div
                    className={`relative aspect-[3/4] rounded-xl bg-card overflow-hidden border cursor-pointer transition-all duration-300 ${
                      isHovered
                        ? "shadow-lg border-border dark:border-primary/40"
                        : "border-border dark:border-border"
                    }`}
                    onClick={() =>
                      hasEnteredViewport &&
                      !loadingGallery &&
                      handleCustomize(dress as MiniGalleryDress)
                    }
                    onMouseEnter={() => setHoveredCardIndex(idx)}
                    onMouseLeave={() => setHoveredCardIndex(null)}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      !hasEnteredViewport || loadingGallery
                        ? "Loading design"
                        : `Customize: ${dress?.prompt || "Dress design"}`
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (hasEnteredViewport && !loadingGallery) {
                          handleCustomize(dress as MiniGalleryDress);
                        }
                      }
                    }}
                  >
                    {!hasEnteredViewport ? (
                      <div className="absolute inset-0 bg-border dark:bg-muted" />
                    ) : loadingGallery ? (
                      <div className="absolute inset-0 animate-pulse bg-border dark:bg-muted" />
                    ) : dress?.image_url ? (
                      <Image
                        src={dress.image_url}
                        alt={dress.prompt || "Dress design"}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 180px, (max-width: 768px) 180px, 180px"
                        loading="lazy"
                        fetchPriority="low"
                        quality={75}
                      />
                    ) : dress?.original_image_path ? (
                      <Image
                        src={getPublicImageUrl(dress.original_image_path)}
                        alt={dress.prompt || "Dress design"}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 180px, (max-width: 768px) 180px, 180px"
                        loading="lazy"
                        fetchPriority="low"
                        quality={75}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                        No image
                      </div>
                    )}
                    {/* Subtle overlay for better visual feedback */}
                    {hasEnteredViewport && !loadingGallery && (
                      <div
                        className={`absolute inset-0 transition-colors duration-300 pointer-events-none ${
                          isHovered ? "bg-black/5" : "bg-black/0"
                        }`}
                      />
                    )}
                  </div>
                  {hasEnteredViewport && !loadingGallery && dress?.prompt && (
                    <p className="mt-2 text-xs text-foreground opacity-70 line-clamp-1">
                      {dress.prompt}
                    </p>
                  )}
                  {hasEnteredViewport && !loadingGallery ? (
                    <Link
                      href={galleryCustomizeHref(
                        dress as MiniGalleryDress,
                        "mini_gallery"
                      )}
                      className="mt-2 flex min-h-11 items-center justify-center rounded-md border border-border px-3 py-2 text-center text-xs font-semibold text-foreground transition-colors hover:border-primary/50 hover:bg-muted/40"
                    >
                      Customize
                    </Link>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
